import { useMemo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMenuItems } from "../queries/menuQueries";
import { formatPrice } from "../utils/currency";
import { getMenuUrl, useRestaurantId } from "../utils/restaurant";
import {
  Container,
  Title,
  Stack,
  Paper,
  Group,
  Text,
  Badge,
  Center,
  Button,
} from "@mantine/core";
import { IconReceipt } from "@tabler/icons-react";

interface LocalOrder {
  id: string;
  total_amount: number;
  status: string;
  created_on: string;
  items: Array<{
    id: string;
    quantity: number;
    menu_item_id: string;
    unit_price: number;
    subtotal: number;
  }>;
  pickup_code?: string | null;
  pickup_code_expires_at?: string | null;
  picked_up?: boolean;
}

const statusColors: Record<string, string> = {
  pending: "yellow",
  confirmed: "cyan",
  preparing: "orange",
  ready: "green",
  completed: "gray",
  cancelled: "red",
};

export default function OrderHistoryPage() {
  const { t } = useTranslation();
  const [localOrders, setLocalOrders] = useState<LocalOrder[]>([]);

  // Get restaurant ID (La Vida)
  const restaurantId = useRestaurantId();

  // Fetch menu items for the selected restaurant (needed for item name lookup)
  const { data: menuItems } = useMenuItems(true, restaurantId);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("orderHistory");
      if (stored) {
        const orders = JSON.parse(stored) as LocalOrder[];
        // Sort by date (newest first)
        const sorted = orders.sort(
          (a, b) =>
            new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
        );
        setLocalOrders(sorted);
      }
    } catch (err) {
      console.error("Failed to load order history:", err);
    }
  }, []);

  const menuById = useMemo(() => {
    const map: Record<string, { name: string }> = {};
    (menuItems || []).forEach((m) => {
      map[m.id] = { name: m.name };
    });
    return map;
  }, [menuItems]);

  const getOrderTitle = (order: LocalOrder) => {
    if (!order.items || order.items.length === 0) {
      return `${t("orders.orderNumber")}${order.id.slice(0, 8)}`;
    }
    const firstItemName =
      menuById[order.items[0].menu_item_id]?.name ||
      `${t("orders.orderNumber")}${order.id.slice(0, 8)}`;
    if (order.items.length === 1) return firstItemName;
    const restCount = order.items.length - 1;
    const restLabel =
      restCount > 1 ? t("orders.itemsPlural") : t("orders.items");
    return `${firstItemName} + ${restCount} ${restLabel}`;
  };

  if (localOrders.length === 0) {
    return (
      <Container py="xl">
        <Title order={1} mb="lg">
          {t("orderHistory.title")}
        </Title>
        <Center h={300}>
          <Stack align="center" gap="md">
            <IconReceipt size={64} stroke={1.5} color="gray" />
            <Text c="dimmed">{t("orderHistory.empty")}</Text>
            <Text size="sm" c="dimmed">
              {t("orderHistory.emptyDescription")}
            </Text>
            <Button
              component={Link}
              to={getMenuUrl(restaurantId)}
              color="brand"
            >
              {t("orders.browseMenu")}
            </Button>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container py="md">
      <Title order={1} mb="lg">
        {t("orderHistory.title")}
      </Title>

      <Stack gap="md">
        {localOrders.map((order) => (
          <Paper key={order.id} withBorder p="md" data-testid="order-card">
            <Group justify="space-between" mb="sm">
              <div>
                <Text fw={600} size="lg">
                  {getOrderTitle(order)}
                </Text>
                <Text size="sm" c="dimmed">
                  {new Date(order.created_on).toLocaleString()}
                </Text>
              </div>
              <Badge color={statusColors[order.status] || "gray"} size="lg">
                {t(`orders.status.${order.status}`, { defaultValue: order.status })}
              </Badge>
            </Group>

            <Group justify="space-between">
              <Text size="sm">
                {order.items.length}{" "}
                {order.items.length > 1
                  ? t("orders.itemsPlural")
                  : t("orders.items")}
              </Text>
              <Text fw={700} size="lg">
                {formatPrice(order.total_amount)}
              </Text>
            </Group>

            {order.pickup_code && (
              <Group mt="xs">
                <Text size="sm" c="dimmed">
                  {t("orderHistory.pickupCode")}:{" "}
                </Text>
                <Text size="sm" fw={600} style={{ fontFamily: "monospace" }} data-testid="pickup-code">
                  {order.pickup_code}
                </Text>
              </Group>
            )}
          </Paper>
        ))}
      </Stack>
    </Container>
  );
}

